// apps/web/src/pages/cadastros/materiais-form.tsx — Fase 03, Plano 08 (CAD-04).
import React, { useEffect, useState } from 'react';
import { AlertCircle, Save } from 'lucide-react';
import { FormDialog, FormDialogContent, FormDialogHeader, FormDialogTitle, FormDialogDescription, FormDialogFooter } from '../../components/ui/form-dialog';
import type { MaterialEntity, ParametrosEstoqueMaterial, SalvarParametrosEstoqueInput } from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';
import { CampoEstoqueMinimo, ID_CAMPO_ESTOQUE_MINIMO } from '../../components/estoque/campo-estoque-minimo';
import { LocalizacaoSelect } from '../../components/estoque/localizacao-select';
import {
  estoqueMinimoParaCampo,
  fatorParaCampo,
  lerEstoqueMinimoDigitado,
  lerFatorDigitado,
  mensagemErroParametros,
  textoAjudaFator,
  unidadesIguais,
  validarParametrosEstoque,
} from '../../lib/estoque/parametros';
import { obterSessaoAtual } from '../../lib/session';
import { lerErroApi } from '../../lib/vendas/erro-api';
import { TipoMaterialCombobox } from './tipo-material-combobox';
import { MaterialDesenhosSection } from './material-desenhos-section';
import { CampoSelect } from '../../components/ui/campo-select';

interface MaterialFormModalProps {
  material?: MaterialEntity | null;
  onClose: () => void;
  /** Na criação recebe o material salvo (a conferência de NF de compra seleciona o recém-criado). */
  onSuccess: (salvo?: MaterialEntity) => void;
}

export const MaterialFormModal: React.FC<MaterialFormModalProps> = ({
  material,
  onClose,
  onSuccess,
}) => {
  const [codigo, setCodigo] = useState(material?.codigo ?? '');
  const [nome, setNome] = useState(material?.nome ?? '');
  const [kind, setKind] = useState<'PRODUTO' | 'INSUMO'>((material?.kind as 'PRODUTO' | 'INSUMO') ?? 'PRODUTO');
  const [categoriaTecnicaId, setCategoriaTecnicaId] = useState<string>(material?.categoriaTecnicaId ?? '');
  const [ativo, setAtivo] = useState(material?.ativo ?? true);

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Seção "Estoque e compras" (Fase 13, D-10/D-03/D-21): só na edição e só com estoque:configurar.
  const [podeConfigurarEstoque, setPodeConfigurarEstoque] = useState(false);
  const [parametros, setParametros] = useState<ParametrosEstoqueMaterial | null>(null);
  const [parametrosErro, setParametrosErro] = useState<string | null>(null);
  const [unidadeCompra, setUnidadeCompra] = useState('');
  const [fator, setFator] = useState('');
  const [preferencialId, setPreferencialId] = useState('');
  const [altoGiro, setAltoGiro] = useState(false);
  const [estoqueMinimo, setEstoqueMinimo] = useState('');
  const [estoqueMinimoErro, setEstoqueMinimoErro] = useState<string | null>(null);
  // O cadastro base já foi gravado mas os parâmetros falharam: fechar deve atualizar a lista.
  const [baseJaSalva, setBaseJaSalva] = useState(false);

  useEffect(() => {
    if (!material?.id) return;
    let vivo = true;
    void obterSessaoAtual().then(async (sessao) => {
      if (!vivo || !sessao.autenticado || !sessao.usuario.permissions.includes('estoque:configurar')) return;
      setPodeConfigurarEstoque(true);
      try {
        const res = await fetch(`/api/v1/estoque/materiais/${material.id}/parametros`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const dados = (await res.json()) as ParametrosEstoqueMaterial;
        if (!vivo) return;
        setParametros(dados);
        setUnidadeCompra(dados.unidadeCompra ?? '');
        setFator(fatorParaCampo(dados.fatorConversao));
        setPreferencialId(dados.estoquePreferencialId);
        setAltoGiro(dados.altoGiroSemDevolucao);
        setEstoqueMinimo(estoqueMinimoParaCampo(dados.estoqueMinimo ?? null));
      } catch {
        if (vivo) setParametrosErro('Não foi possível carregar os parâmetros de estoque deste material.');
      }
    });
    return () => {
      vivo = false;
    };
  }, [material?.id]);

  const unidadeEstoque = parametros?.unidadeEstoque ?? '';
  const mesmaUnidade = unidadesIguais(unidadeEstoque, unidadeCompra);
  const secaoMudou =
    parametros !== null &&
    (unidadeCompra.trim() !== (parametros.unidadeCompra ?? '') ||
      (!mesmaUnidade && fator.trim() !== fatorParaCampo(parametros.fatorConversao)) ||
      preferencialId !== parametros.estoquePreferencialId ||
      altoGiro !== parametros.altoGiroSemDevolucao ||
      estoqueMinimo.trim() !== estoqueMinimoParaCampo(parametros.estoqueMinimo ?? null));

  /** Grava os parâmetros de estoque (PUT com versão). Devolve a mensagem de erro ou null. */
  const salvarParametros = async (materialId: string): Promise<string | null> => {
    if (!parametros) return null;
    const fatorLido = lerFatorDigitado(fator);
    const minimoLido = lerEstoqueMinimoDigitado(estoqueMinimo);
    const corpo: SalvarParametrosEstoqueInput = {
      versao: parametros.versao,
      unidadeCompra: unidadeCompra.trim() === '' ? null : unidadeCompra.trim(),
      fatorConversao: mesmaUnidade || fatorLido.tipo !== 'ok' ? null : fatorLido.valor,
      estoquePreferencialId: preferencialId === '' ? null : preferencialId,
      altoGiroSemDevolucao: altoGiro,
      estoqueMinimo: minimoLido.tipo === 'ok' ? minimoLido.valor : null,
    };
    const res = await apiMutate(`/api/v1/estoque/materiais/${materialId}/parametros`, { method: 'PUT', body: corpo });
    if (res.ok) return null;
    const erro = await lerErroApi(res, 'Falha ao salvar os parâmetros de estoque.');
    return mensagemErroParametros(erro.codigo) ?? erro.mensagem;
  };

  const fecharModal = () => {
    if (baseJaSalva) onSuccess();
    else onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!codigo.trim() || !nome.trim()) {
      const faltantes = [!codigo.trim() ? 'Código' : null, !nome.trim() ? 'Descrição do Material' : null].filter(Boolean);
      setErrorMsg(`Falta preencher neste formulário: ${faltantes.join(' e ')}.`);
      document.getElementById(!codigo.trim() ? 'materialCodigo' : 'materialNome')?.focus();
      return;
    }

    const enviarParametros = podeConfigurarEstoque && parametros !== null && secaoMudou;
    if (enviarParametros) {
      const problemas = validarParametrosEstoque({
        unidadeEstoque,
        unidadeCompra,
        fator,
        tipoTinta: parametros.tipoTinta,
        altoGiro,
      });
      const minimoLido = lerEstoqueMinimoDigitado(estoqueMinimo);
      const minimoInvalido = minimoLido.tipo === 'invalido' ? minimoLido.mensagem : null;
      setEstoqueMinimoErro(minimoInvalido);
      if (problemas.length > 0) {
        setErrorMsg(problemas[0] ?? null);
        return;
      }
      if (minimoInvalido) {
        setErrorMsg(null);
        document.getElementById(ID_CAMPO_ESTOQUE_MINIMO)?.focus();
        return;
      }
    }

    setSaving(true);
    setErrorMsg(null);

    const payload = {
      codigo: codigo.trim().toUpperCase(),
      nome: nome.trim(),
      kind,
      categoriaTecnicaId: categoriaTecnicaId || undefined,
      ativo,
    };

    try {
      const url = material?.id ? `/api/v1/cadastros/materiais/${material.id}` : '/api/v1/cadastros/materiais';
      const method = material?.id ? 'PATCH' : 'POST';

      const res = await apiMutate(url, {
        method,
        body: payload,
      });

      if (!res.ok) {
        const erro = await lerErroApi(res, 'Falha ao salvar material');
        throw new Error(erro.mensagem);
      }
      const salvo = material?.id ? undefined : ((await res.json().catch(() => null)) as MaterialEntity | null) ?? undefined;

      // D-29: primeiro o cadastro base, depois os parâmetros de estoque (PUT com versão).
      if (enviarParametros && material?.id) {
        setBaseJaSalva(true);
        const falha = await salvarParametros(material.id);
        if (falha) {
          setErrorMsg(`O cadastro do material foi salvo, mas os parâmetros de estoque não: ${falha}`);
          return;
        }
      }

      onSuccess(salvo);
      onClose();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Erro ao processar formulário');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog open onOpenChange={(aberto) => { if (!aberto) fecharModal(); }}>
      <FormDialogContent>
        <form onSubmit={(event) => { void handleSubmit(event); }}>
          <FormDialogHeader>
            <FormDialogTitle>
              {material?.id ? 'Editar Material' : 'Novo Material'}
            </FormDialogTitle>
            <FormDialogDescription>Identificação do material e parâmetros para produção, estoque e compras.</FormDialogDescription>
          </FormDialogHeader>
          <div className="space-y-4">
          {errorMsg && (
            <div role="alert" className="p-3 bg-rose-50 border border-rose-200 rounded-md text-rose-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label htmlFor="materialCodigo" className="font-medium text-slate-700">Código</label>
              <input
                id="materialCodigo"
                type="text"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                placeholder="Ex: MAT-001"
                className="w-full px-3 py-2 border rounded-md font-mono text-sm text-slate-900 focus:ring-1 focus:ring-slate-900"
                required
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="materialTipo" className="font-medium text-slate-700">Tipo de Item</label>
              <CampoSelect
                id="materialTipo"
                value={kind}
                onValueChange={(valor) => setKind(valor as 'PRODUTO' | 'INSUMO')}
                options={[
                  { value: 'PRODUTO', label: 'Produto (Peça de Cliente)' },
                  { value: 'INSUMO', label: 'Insumo / Matéria-Prima' },
                ]}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label htmlFor="materialNome" className="font-medium text-slate-700">Descrição do Material</label>
            <input
              id="materialNome"
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Parafuso Sextavado M8x40 Aço 8.8"
              className="w-full px-3 py-2 border rounded-md text-sm text-slate-900 focus:ring-1 focus:ring-slate-900"
              required
            />
          </div>

          {/* Categoria Técnica Selecionada via Combobox (CAD-04) */}
          <div className="space-y-1">
            <label className="font-semibold text-slate-700">
              Categoria técnica
            </label>
            <TipoMaterialCombobox
              value={categoriaTecnicaId}
              onChange={(id) => setCategoriaTecnicaId(id)}
            />
            <p className="text-[11px] text-slate-400">
              Classificação técnica oficial para roteiros e tratamento superficial.
            </p>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="materialAtivo"
              checked={ativo}
              onChange={(e) => setAtivo(e.target.checked)}
              className="rounded border-slate-300 text-slate-900 focus:ring-slate-900"
            />
            <label htmlFor="materialAtivo" className="font-medium text-slate-700 select-none">
              Material ativo para novas ordens e apontamentos
            </label>
          </div>

          {material?.id && podeConfigurarEstoque && (
            <fieldset className="space-y-3 pt-3 border-t">
              <legend className="font-semibold text-slate-900 text-sm pr-2">Estoque e compras</legend>
              {parametrosErro && <p className="text-rose-700">{parametrosErro}</p>}
              {parametros === null && !parametrosErro && <p className="text-slate-400">Carregando parâmetros de estoque...</p>}
              {parametros && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label htmlFor="materialUnidadeEstoque" className="font-semibold text-slate-700">
                        Unidade de estoque / consumo
                      </label>
                      <input
                        id="materialUnidadeEstoque"
                        type="text"
                        value={parametros.unidadeEstoque}
                        readOnly
                        className="w-full px-3 py-2 border rounded-md text-sm text-slate-600 bg-slate-100"
                      />
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="materialUnidadeCompra" className="font-semibold text-slate-700">
                        Unidade de compra
                      </label>
                      <input
                        id="materialUnidadeCompra"
                        type="text"
                        value={unidadeCompra}
                        onChange={(e) => setUnidadeCompra(e.target.value)}
                        placeholder={parametros.unidadeEstoque}
                        maxLength={20}
                        className="w-full px-3 py-2 border rounded-md text-sm text-slate-900 focus:ring-1 focus:ring-slate-900"
                      />
                    </div>
                  </div>

                  {!mesmaUnidade && (
                    <div className="space-y-1">
                      <label htmlFor="materialFatorConversao" className="font-semibold text-slate-700">
                        Fator de conversão
                      </label>
                      <input
                        id="materialFatorConversao"
                        type="text"
                        inputMode="decimal"
                        value={fator}
                        onChange={(e) => setFator(e.target.value)}
                        aria-describedby="materialFatorAjuda"
                        className="w-full px-3 py-2 border rounded-md font-mono text-sm text-slate-900 focus:ring-1 focus:ring-slate-900"
                      />
                      <p id="materialFatorAjuda" className="text-[11px] text-slate-400">
                        {textoAjudaFator(parametros.unidadeEstoque, unidadeCompra)}
                      </p>
                    </div>
                  )}

                  <div className="space-y-1">
                    <label htmlFor="materialEstoquePreferencial" className="font-semibold text-slate-700">
                      Estoque preferencial
                    </label>
                    <LocalizacaoSelect
                      id="materialEstoquePreferencial"
                      value={preferencialId}
                      onChange={setPreferencialId}
                      rotulo="Estoque preferencial"
                    />
                  </div>

                  <CampoEstoqueMinimo
                    unidade={parametros.unidadeEstoque}
                    valor={estoqueMinimo}
                    erro={estoqueMinimoErro}
                    desabilitado={saving}
                    onChange={(valor) => {
                      setEstoqueMinimo(valor);
                      setEstoqueMinimoErro(null);
                    }}
                  />

                  {parametros.tipoTinta && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="materialAltoGiro"
                          checked={altoGiro}
                          onChange={(e) => setAltoGiro(e.target.checked)}
                          className="rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                        />
                        <label htmlFor="materialAltoGiro" className="font-medium text-slate-700 select-none">
                          Alto giro, sem devolução
                        </label>
                      </div>
                      <p className="text-[11px] text-slate-400">
                        Marque para cores que não retornam ao estoque (ex.: cinza e preto). O consumo é rateado por área pintada, por dia.
                      </p>
                    </div>
                  )}
                </>
              )}
            </fieldset>
          )}

          {material?.id && <MaterialDesenhosSection materialId={material.id} />}

          </div>
          <FormDialogFooter>
            <button
              type="button"
              onClick={fecharModal}
              className="px-4 py-2 border rounded-md font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white rounded-md font-semibold hover:bg-slate-800 disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Salvando...' : 'Salvar Material'}
            </button>
          </FormDialogFooter>
        </form>
      </FormDialogContent>
    </FormDialog>
  );
};
